import React, { ReactNode, useEffect, useState } from 'react';
import classNames from 'classnames';
import { SelectionListCommentOut } from '../../store/api';
import { useTranslation } from 'react-i18next';
import { useIsAdmin } from '../../shared/utils';
import { useKeycloak } from '../../../useKeycloakAndInit';
import { useAuthentication } from '../../app-store/authentication/useAuthentication';

type Props = {
    readonly entry: SelectionListCommentOut;
    readonly pendingEditRequest?: boolean;
    readonly onCommentConfirmEdit: (entryId: number, text: string) => void;
    readonly showCommentDeleteConfirmation: (userId: string, entryId: number) => void;
};

export const SelectionlistComment = (props: Props) => {
    const { t } = useTranslation();
    const [editText, setEditText] = useState('');
    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const kc = useKeycloak();
    const isAdmin = useIsAdmin();
    const { spoc } = useAuthentication();

    useEffect(() => {
        if (props.pendingEditRequest) {
            setIsEditMode(false);
        }
    }, [props.pendingEditRequest]);

    function onCommentEditClick() {
        setIsEditMode(true);
        setEditText(props.entry.comment ?? '');
    }

    function onCommentEditAbort() {
        setEditText('');
        setIsEditMode(false);
    }

    const entry = props.entry;

    let commentMenu: ReactNode = null;
    if (isEditMode) {
        commentMenu = (
            <div className="sl-comment-menu fl-right">
                {props.pendingEditRequest ? <i className="sl-pending-icon" /> : null}
                <a className="sl-comment-operation" onClick={() => props.onCommentConfirmEdit(entry.id!, editText)}>
                    <i className="material-icons">check</i>
                </a>
                <a className="sl-comment-operation" onClick={onCommentEditAbort}>
                    <i className="material-icons">close</i>
                </a>
            </div>
        );
    } else if ((kc.tokenParsed as any)?.preferred_username === entry.userId || isAdmin) {
        commentMenu = (
            <div className="sl-comment-menu fl-right">
                <a className="sl-comment-operation" onClick={onCommentEditClick}>
                    <i className="material-icons">mode_edit</i>
                </a>
                <a
                    className="sl-comment-operation"
                    onClick={() => props.showCommentDeleteConfirmation(entry.userId!, entry.id!)}
                >
                    <i className="material-icons">delete</i>
                </a>
            </div>
        );
    }

    return (
        <li key={`comment-${entry.id}`} className="sl-comments-entry sl-comment">
            <strong>{entry.userName}</strong>&nbsp;
            {entry.admin ? <span className="bolinda-tag">Bolinda</span> : <span>{spoc.name}</span>}
            {commentMenu}
            <div className="sl-comment-timestamp">
                {entry.creDat
                    ? `${t('dateFormat.date', {
                          val: entry.creDat ?? 0
                      })} ${t('dateFormat.time', {
                          val: entry.creDat ?? 0
                      })}`
                    : '-'}
            </div>
            {isEditMode ? (
                <div className="sl-comment-input-container">
                    <textarea
                        className={classNames('sl-comment-input', 'sl-edit-text', {
                            pending: props.pendingEditRequest
                        })}
                        readOnly={false}
                        value={editText}
                        onChange={e => setEditText(e.target.value)}
                    />
                    <div className="sl-comment-input-size sl-edit-text">{editText}</div>
                </div>
            ) : (
                <div className="sl-comment-text">{entry.comment}</div>
            )}
        </li>
    );
};

SelectionlistComment.defaultProps = {
    entry: null,
    isEditMode: false
};
